import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
  forwardRef,
  useImperativeHandle,
} from 'react'
import './Core.css'
import { StoreContext } from '@store/Root.store'
import { LoadingScreen } from './LoadingScreen'
import { PdfErrorContainer } from './PdfErrorContainer'
import { createDocumentOptions } from './methods'
import { devLog } from '@/methods/devLog'
import { PdfViewerProps, PdfViewerRef } from '../types'

import * as PDFJS from 'pdfjs-dist'
import {
  PDFViewer,
  EventBus,
  PDFLinkService,
  PDFFindController,
  LinkTarget,
} from 'pdfjs-dist/web/pdf_viewer.mjs'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'
import type { PDFViewerOptions } from 'pdfjs-dist/types/web/pdf_viewer'
import WORKER_SRC from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker&url'
import 'pdfjs-dist/web/pdf_viewer.css'

const SCALE_OFFSET_ON_AUTORESIZE = 0.65

// Initialize worker once globally to prevent re-initialization on rerenders
let workerInitialized = false
const initializeWorker = () => {
  if (workerInitialized) return
  try {
    PDFJS.GlobalWorkerOptions.workerPort = new Worker(WORKER_SRC, {
      type: 'module',
    })
  } catch {
    // Fallback for environments that don't support module workers
  }
  PDFJS.GlobalWorkerOptions.workerSrc = WORKER_SRC
  workerInitialized = true
}

enum RenderTextMode {
  DISABLED = 0,
  ENABLED = 1,
  ENHANCED = 2,
}

export const Renderer = forwardRef<PdfViewerRef, PdfViewerProps>(
  (
    {
      source,
      onDocumentLoadSuccess,
      onError,
      height,
      width,
      // onDocumentLastPage,
      setAllowSigning,
      initialZoom = 1,
      minZoom = 0.1,
      maxZoom = 5,
      zoomStep = 0.1,
    },
    ref
  ) => {
    const store = useContext(StoreContext)
    const { pdfViewer: trans } = store.TranslationsState.translationsNew

    const containerRef = useRef<HTMLDivElement>(null)
    const pdfViewerRef = useRef<PDFViewer | null>(null)
    const pdfDocRef = useRef<PDFDocumentProxy | null>(null)
    const eventBusRef = useRef<EventBus | null>(null)
    const linkServiceRef = useRef<PDFLinkService | null>(null)
    const findControllerRef = useRef<PDFFindController | null>(null)
    const [error, setError] = useState<string | null>(null)

    const [currentPage, setCurrentPage] = useState(1)
    const [numPages, setNumPages] = useState(0)
    const [zoom, setZoom] = useState(initialZoom)
    const [isLoading, setIsLoading] = useState(true)
    const [isLoaded, setIsLoaded] = useState(false)

    // Track if component is mounted to prevent state updates after unmount
    const isMountedRef = useRef(true)
    // Track current source to detect changes
    const currentSourceRef = useRef(source)

    // Initialize PDF.js worker once
    useEffect(() => {
      initializeWorker()
    }, [])

    // Initialize viewer
    const initViewer = useCallback(() => {
      if (!containerRef.current) return

      // Clean up existing viewer before creating a new one
      if (pdfViewerRef.current) {
        pdfViewerRef.current.cleanup?.()
        pdfViewerRef.current = null
      }

      eventBusRef.current = new EventBus()

      linkServiceRef.current = new PDFLinkService({
        eventBus: eventBusRef.current,
        externalLinkTarget: LinkTarget.BLANK,
      })

      findControllerRef.current = new PDFFindController({
        eventBus: eventBusRef.current,
        linkService: linkServiceRef.current,
      })

      const viewerOptions: PDFViewerOptions = {
        eventBus: eventBusRef.current,
        container: containerRef.current,
        removePageBorders: true,
        linkService: linkServiceRef.current,
        textLayerMode: RenderTextMode.ENABLED,
        findController: findControllerRef.current,
        annotationMode: PDFJS.AnnotationMode.ENABLE,
      }

      pdfViewerRef.current = new PDFViewer(viewerOptions)
      linkServiceRef.current.setViewer(pdfViewerRef.current)
    }, [])

    // Load document
    const loadDocument = useCallback(async () => {
      if (!isMountedRef.current) return

      try {
        setIsLoading(true)
        setError(null)

        const options = await createDocumentOptions(source)
        const loadingTask = PDFJS.getDocument(options)
        const pdfDoc = await loadingTask.promise

        // Check if component is still mounted before updating state
        if (!isMountedRef.current) {
          pdfDoc.destroy()
          return
        }

        // Destroy previous document if exists
        if (pdfDocRef.current) {
          pdfDocRef.current.destroy()
        }

        pdfDocRef.current = pdfDoc
        setNumPages(pdfDoc.numPages)

        if (pdfViewerRef.current && findControllerRef.current && linkServiceRef.current) {
          findControllerRef.current.setDocument(pdfDoc)
          linkServiceRef.current.setDocument(pdfDoc)
          pdfViewerRef.current.setDocument(pdfDoc)
        }

        // Wait for initial render
        setTimeout(() => {
          if (!isMountedRef.current) return
          setIsLoading(false)
          setIsLoaded(true)
          updateSize()
          onDocumentLoadSuccess?.(pdfDoc.numPages)
        }, 200)
      } catch (err) {
        if (!isMountedRef.current) return
        const error = err as Error
        setError(error.message || 'Failed to load PDF')
        setIsLoading(false)
        onError?.(error)
        devLog('Error loading PDF document:', error)
      }
    }, [source, onDocumentLoadSuccess, onError])

    // #ANCHOR - scroll
    // Handle scroll for title visibility
    const handleScroll = useCallback(() => {
      if (!containerRef.current || !pdfViewerRef.current) return

      const currentPageNumber = pdfViewerRef.current._currentPageNumber
      if (currentPageNumber !== currentPage) {
        setCurrentPage(currentPageNumber)
      }
    }, [currentPage])

    const getScale = useCallback((viewportWidth: number) => {
      const containerWidth = pdfViewerRef.current?.container.clientWidth || 0
      if (containerWidth === 0 || viewportWidth === 0) return 1
      const ratio = containerWidth / viewportWidth
      // Use scale 1 as base for fit-to-width calculation (not current zoom)
      return ratio * SCALE_OFFSET_ON_AUTORESIZE
    }, [])

    // Update size to fit container
    const updateSize = useCallback(
      (setFullWidth = false) => {
        if (!pdfDocRef.current || !pdfViewerRef.current || !isMountedRef.current) return

        const currentScale = pdfViewerRef.current.currentScale || 1

        pdfDocRef.current
          .getPage(pdfViewerRef.current.currentPageNumber)
          .then((page: PDFPageProxy) => {
            // Check refs are still valid after async operation
            if (!pdfViewerRef.current || !isMountedRef.current) return

            const rotation = pdfViewerRef.current.pagesRotation || page.rotate
            const viewportWidth = page.getViewport({ scale: currentScale, rotation }).width

            let scale = currentScale
            const containerWidth = pdfViewerRef.current.container.clientWidth

            if (setFullWidth || viewportWidth > containerWidth) {
              const viewPort = page.getViewport({ scale: 1, rotation })
              scale = getScale(viewPort.width)
            }

            setZoom(scale)
            if (pdfViewerRef.current) {
              pdfViewerRef.current.currentScale = scale
            }
          })
          .catch((err) => {
            devLog('updateSize error:', err)
          })
      },
      [getScale]
    )

    // Navigation functions
    const goToPage = useCallback(
      (page: number) => {
        if (!pdfViewerRef.current || !isLoaded) return

        let targetPage = page
        if (targetPage < 1) targetPage = 1
        if (targetPage > numPages) targetPage = numPages

        setCurrentPage(targetPage)
        pdfViewerRef.current.scrollPageIntoView({ pageNumber: targetPage })
      },
      [numPages, isLoaded]
    )

    const nextPage = useCallback(() => {
      goToPage(currentPage + 1)
    }, [currentPage, goToPage])

    const prevPage = useCallback(() => {
      goToPage(currentPage - 1)
    }, [currentPage, goToPage])

    // Zoom functions
    const changeZoom = useCallback(
      (newZoom: number) => {
        let clampedZoom = newZoom
        if (clampedZoom < minZoom) clampedZoom = minZoom
        if (clampedZoom > maxZoom) clampedZoom = maxZoom

        setZoom(clampedZoom)
        if (pdfViewerRef.current) {
          pdfViewerRef.current.currentScale = clampedZoom
        }
      },
      [minZoom, maxZoom]
    )

    const zoomIn = useCallback(() => {
      changeZoom(zoom + zoomStep)
    }, [zoom, zoomStep, changeZoom])

    const zoomOut = useCallback(() => {
      changeZoom(zoom - zoomStep)
    }, [zoom, zoomStep, changeZoom])

    // Rotate function
    const rotate = useCallback(() => {
      if (!pdfViewerRef.current) return

      let rotation = pdfViewerRef.current.pagesRotation
      rotation += 90
      if (rotation > 270) rotation = 0
      pdfViewerRef.current.pagesRotation = rotation
    }, [])

    // Fit to width
    const fitToWidth = useCallback(() => {
      setZoom(1)
      updateSize(true)
    }, [updateSize])

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        goToPage,
        nextPage,
        prevPage,
        setZoom: changeZoom,
        zoomIn,
        zoomOut,
        rotate,
        fitToWidth,
        getCurrentPage: () => currentPage,
        getPageCount: () => numPages,
      }),
      [
        goToPage,
        nextPage,
        prevPage,
        changeZoom,
        zoomIn,
        zoomOut,
        rotate,
        fitToWidth,
        currentPage,
        numPages,
      ]
    )

    // Initialize and load on mount
    useEffect(() => {
      isMountedRef.current = true
      initViewer()
      loadDocument()

      return () => {
        isMountedRef.current = false

        // Clean up PDF document
        if (pdfDocRef.current) {
          pdfDocRef.current.destroy()
          pdfDocRef.current = null
        }

        // Clean up viewer
        if (pdfViewerRef.current) {
          pdfViewerRef.current.cleanup?.()
          pdfViewerRef.current = null
        }

        // Clean up link service
        if (linkServiceRef.current) {
          linkServiceRef.current.setViewer(null)
          linkServiceRef.current.setDocument(null)
          linkServiceRef.current = null
        }

        // Clean up find controller
        if (findControllerRef.current) {
          findControllerRef.current = null
        }

        // Clean up event bus
        eventBusRef.current = null
      }
    }, [])

    // Reload when source changes
    useEffect(() => {
      // Compare sources to detect actual changes
      const sourceChanged = JSON.stringify(currentSourceRef.current) !== JSON.stringify(source)
      currentSourceRef.current = source

      if (isLoaded && sourceChanged) {
        // Destroy previous document before loading new one
        if (pdfDocRef.current) {
          pdfDocRef.current.destroy()
          pdfDocRef.current = null
        }
        setIsLoaded(false)
        loadDocument()
      }
    }, [source, isLoaded, loadDocument])

    // Setup scroll listener
    useEffect(() => {
      const container = containerRef.current
      if (container) {
        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
      }
    }, [handleScroll])

    // Setup resize listener
    useEffect(() => {
      if (!isLoaded) return

      let timeoutId: number
      const handleResize = () => {
        clearTimeout(timeoutId)
        timeoutId = window.setTimeout(() => {
          updateSize()
        }, 120)
      }

      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
        clearTimeout(timeoutId)
      }
    }, [isLoaded, updateSize])

    if (error) {
      return (
        <PdfErrorContainer
          width={width}
          height={height}
          failedToLoadPdf={trans?.cantDisplayTheDocument || ''}
          setAllowSigning={setAllowSigning}
          documentUrl={source.type === 'url' ? source.url : undefined}
        />
      )
    }

    return (
      <div
        className="pdf-ocg-core__viewer-container"
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
        }}
        data-testid="pdf-viewer"
        data-loaded={!isLoading}
      >
        {isLoading && (
          <LoadingScreen width={width} height={height}>
            <div className="pdf-ocg-core__loading-text">{trans?.loading}</div>
          </LoadingScreen>
        )}
        <div
          className="pdf-ocg-core__viewer-inner-container"
          ref={containerRef}
          id="pdf-viewer-container"
        >
          <div className="pdf-ocg-core__pdf-viewer-inner pdfViewer" />
        </div>
      </div>
    )
  }
)
